import { useState, useEffect, useRef } from 'react';
import i18n from "../i18n";
import { toast } from 'react-toastify';
import { useRazorpay } from "react-razorpay";
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosInstance';
import planPurchaseService from '../services/planPurchaseService';
import { PLAN_HIERARCHY } from '../services/planService';
import { isYearlyPlan, crossedOutPrice } from '../utils/planPricing';
import '../assets/css/PurchasePlan.css';
import { FEATURES, TAMIL_NAMES } from '../utils/planFeatures';
import { capturePendingPlan, clearPendingPlan, savePendingPayment, getPendingPayment, clearPendingPayment, pendingPaymentAge, PENDING_PAYMENT_GIVE_UP_MS } from '../utils/pendingPlan';
import { loadRazorpayCheckout, CHECKOUT_UNAVAILABLE_MESSAGE } from '../utils/razorpayCheckout';

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTION - Format number with commas
// ═══════════════════════════════════════════════════════════════
const formatPrice = (price) => {
    if (!price) return '0';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// "Offer ends in 2d 4h" under a discounted price; red in the last hour
const timeLeftUntil = (endDate) => {
    const diff = new Date(endDate) - new Date();
    if (diff <= 0) return { text: 'Expired', urgent: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return {
        text: days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
        urgent: diff < 1000 * 60 * 60,
    };
};

const CountdownTimer = ({ endDate }) => {
    // Worked out on the first render too, so the line never shows without a time
    const [left, setLeft] = useState(() => timeLeftUntil(endDate));

    useEffect(() => {
        setLeft(timeLeftUntil(endDate));
        const interval = setInterval(() => setLeft(timeLeftUntil(endDate)), 60000); // Update every minute
        return () => clearInterval(interval);
    }, [endDate]);

    return <span className={`pp-count${left.urgent ? ' urgent' : ''}`}>Offer ends in {left.text}</span>;
};

const TIERS = [0, 1, 2];
const POPULAR_TIER = 1;

// Checking a plan payment the checkout handler missed (mobile UPI): about a minute
const RECONCILE_RETRY_MS = 4000;
const RECONCILE_RETRIES = 15;
// Focus / visibility changes check at most this often (each check asks Razorpay)
const RECONCILE_THROTTLE_MS = 30000;
// On page load, the follow-up checks run only for a checkout opened this recently:
// the tab reloaded while the seller was in the UPI app. An older pending order
// (an abandoned checkout) gets one check per visit
const RECENT_CHECKOUT_MS = 10 * 60 * 1000;

// What a plan costs a year: the offer price while a discount runs, else the list price
const payablePrice = (plan) => (plan.has_discount && plan.discount ? plan.discount.final_price : plan.original_price || plan.price);

const featureValue = (value) => {
    if (value === 1) return <span className="yes"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-label="Included" role="img"><path d="m5 12 5 5 9-10" /></svg></span>;
    if (value === 0) return <span className="no"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-label="Not included" role="img"><path d="M6 6l12 12M18 6 6 18" /></svg></span>;
    return value;
};

function Plan() {
    const { Razorpay } = useRazorpay();
    const navigate = useNavigate();
    
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [businessUserId, setBusinessUserId] = useState(null);
    const [currentPlanId, setCurrentPlanId] = useState(null);
    const [currentPlanInfo, setCurrentPlanInfo] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingPurchase, setPendingPurchase] = useState(null);
    // Set once /business/plan/info has answered, so a picked plan is validated against it
    const [currentPlanLoaded, setCurrentPlanLoaded] = useState(false);
    // The plan picked on mytamilkart.com (?plan=, or kept through sign up and login):
    // its checkout opens once on arrival; Get Started on its card tries again
    const [pickedPlanId] = useState(() => capturePendingPlan());
    const pickedPlanHandled = useRef(false);
    // A purchase being validated / ordered: repeat taps (or the picked plan opening
    // at the same time) don't stack a second validation and toast (issue #286)
    const buyingRef = useRef(false);
    // A /plan/payment/reconcile call under way, and when the last one started
    const reconcilingRef = useRef(false);
    const lastReconcileRef = useRef(0);
    // The follow-up checks after the seller comes back (see reconcilePayment)
    const reconcileTimerRef = useRef(null);
    // Set when the plans page goes away: a check still in flight then neither
    // schedules another nor redirects from whatever page the seller is on
    const unmountedRef = useRef(false);
    // A higher plan an existing seller picked on mytamilkart.com: { plan, step }, where
    // step 'ask' is "already has a plan, upgrade?" and 'compare' shows the difference
    const [planSwitch, setPlanSwitch] = useState(null);
    // The seller's number for that dialog, read once like the picked plan
    const [sellerPhone] = useState(() => localStorage.getItem('phone_number'));
    // The comparison step's heading: focused when it opens, not Continue, so Enter on Yes
    // and again can't skip the comparison straight to Razorpay (React's autoFocus is
    // for form controls only)
    const compareHeadingRef = useRef(null);
    const switchStep = planSwitch?.step;
    useEffect(() => {
        if (switchStep === 'compare') compareHeadingRef.current?.focus();
    }, [switchStep]);
    // Compare table: which tiers are shown, and whether rows that are the same in all of them are hidden
    const [shownTiers, setShownTiers] = useState(TIERS);
    const [diffOnly, setDiffOnly] = useState(false);

    useEffect(() => {
        // STEP 1: Get business_user_id from localStorage
        const userIdFromStorage = localStorage.getItem("business_id");
        
        console.log('Initializing plans with userId:', userIdFromStorage);

        if (userIdFromStorage) {
            setBusinessUserId(userIdFromStorage);
            // Fetch current plan info
            fetchCurrentPlan(userIdFromStorage);
            // Fetch plans with discount for this user
            fetchPlans(userIdFromStorage);
        } else {
            // Fallback: Try to read from URL if not in localStorage
            const urlParams = new URLSearchParams(window.location.search);
            const userIdFromUrl = urlParams.get("business_user_id");
            
            if (userIdFromUrl) {
                setBusinessUserId(userIdFromUrl);
                // Store it in localStorage for future use
                localStorage.setItem("business_id", userIdFromUrl);
                // Fetch current plan info
                fetchCurrentPlan(userIdFromUrl);
                // Fetch plans with discount for this user
                fetchPlans(userIdFromUrl);
            } else {
                console.warn('No business_id found in localStorage or URL');
                // No user ID, fetch plans without discounts
                fetchPlans(null);
            }
        }
    }, []);

    const fetchPlans = async (userId) => {
        try {
            // Fetch plans with discounts for this business
            const endpoint = userId 
                ? `/plans/list-with-discounts?business_user_id=${userId}`
                : '/plans/list';
            
            console.log('Fetching plans from:', endpoint);
            const response = await api.get(endpoint);
            console.log('Plans API response:', response.data);
            
            if (response.data.status === 'success') {
                // API returns data.data.plans (not result.plans)
                const fetchedPlans = response.data.data?.plans || response.data.result?.plans || [];
                console.log('Fetched plans:', fetchedPlans);
                setPlans(fetchedPlans);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching plans:', error);
            console.error('Error details:', error.response?.data);
            
            // If discount endpoint fails (404), fallback to regular endpoint
            if (error.response?.status === 404 && userId) {
                console.log('Discount endpoint not found, falling back to regular endpoint');
                try {
                    const fallbackResponse = await api.get('/plans/list');
                    if (fallbackResponse.data.status === 'success') {
                        const fetchedPlans = fallbackResponse.data.data?.plans || fallbackResponse.data.result?.plans || [];
                        setPlans(fetchedPlans);
                    }
                } catch (fallbackError) {
                    console.error('Fallback also failed:', fallbackError);
                    setPlans([]);
                }
            } else {
                setPlans([]);
            }
            setLoading(false);
        }
    };

    // Only yearly plans (validity_days = 365) are offered
    const yearlyPlans = plans.filter(isYearlyPlan);
    // Every price comes from Plan Management; with none set up the page shows a notice
    const filteredPlans = yearlyPlans;
    const pickedPlan = pickedPlanId ? filteredPlans.find(p => String(p.plan_id) === String(pickedPlanId)) : undefined;
    const currentPlan = plans.find(p => String(p.plan_id) === String(currentPlanId));
    const currentPlanName = currentPlan?.name || currentPlanInfo?.plan_name || currentPlanId;

    // [label, current, picked] rows for the plan switch dialog: the yearly price, then
    // the compare table's features that differ (its columns are the three plans in order)
    const planDifferences = (from, to) => {
        const rows = [['Price', from ? `₹${formatPrice(payablePrice(from))}/year` : '—', `₹${formatPrice(payablePrice(to))}/year`]];
        // By plan ID: the plans can be fetched again after the dialog opened
        const tierOf = (plan) => (plan && filteredPlans.length === TIERS.length
            ? filteredPlans.findIndex(p => String(p.plan_id) === String(plan.plan_id)) : -1);
        const fromTier = tierOf(from);
        const toTier = tierOf(to);
        if (fromTier < 0 || toTier < 0) return rows;
        FEATURES.forEach(([, , features]) => features.forEach(row => {
            if (row[fromTier + 1] !== row[toTier + 1]) rows.push([row[0], row[fromTier + 1], row[toTier + 1]]);
        }));
        return rows;
    };

    // Which plan is higher: PLAN_HIERARCHY when it knows both IDs, else the list
    // price. Plan IDs are entered in Plan Management (e.g. "YEARLY PRO PLAN") and
    // needn't be the hierarchy's keys, which would rank every plan level 0.
    // Returns <0 when a is lower than b, >0 when higher, 0 when equal or unknown.
    const comparePlans = (aId, bId) => {
        const levelA = PLAN_HIERARCHY[aId];
        const levelB = PLAN_HIERARCHY[bId];
        if (levelA && levelB) return levelA - levelB;
        const priceOf = (id) => {
            const plan = plans.find(p => String(p.plan_id) === String(id));
            return plan ? Number(plan.original_price || plan.price) : NaN;
        };
        const diff = priceOf(aId) - priceOf(bId);
        return Number.isFinite(diff) ? diff : 0;
    };

    const fetchCurrentPlan = async (userId) => {
        try {
            const response = await api.get(`/business/plan/info?business_user_id=${userId}`);
            if (response.data.status === 'success' && (response.data.result || response.data.data)) {
                const result = response.data.result || response.data.data;
                setCurrentPlanInfo(result);
                // Set current plan ID if user has an active plan
                if (result.plan_id && result.plan_status !== 'none' && !result.is_expired) {
                    setCurrentPlanId(result.plan_id);
                }
            }
        } catch (error) {
            console.error('Error fetching current plan:', error);
        } finally {
            setCurrentPlanLoaded(true);
        }
    };

    // STEP 3: On clicking "Buy Now" → Validate FIRST, then Create Razorpay Order
    // `confirmed`: the seller already agreed to the upgrade (plan switch dialog),
    // so only blockers stop the payment, not the upgrade confirmation
    const handleBuyPlan = async (plan, { confirmed = false } = {}) => {
        // Not logged in (no business_user_id): this portal's login
        if (!businessUserId) {
            navigate(`/login?lang=${i18n.language}`);
            return;
        }

        if (buyingRef.current) return;
        buyingRef.current = true;
        try {
            console.log('🔍 Validating plan purchase:', plan.plan_id);
            
            // ═══════════════════════════════════════════════════════════════
            // STEP 3A: VALIDATE PURCHASE SCENARIO (Frontend Validation)
            // ═══════════════════════════════════════════════════════════════
            
            // Get current usage data
            const [productCount, domainCount] = await Promise.all([
                planPurchaseService.getCurrentProductCount(businessUserId),
                planPurchaseService.getCurrentDomainCount(businessUserId)
            ]);

            console.log('📊 Current usage:', { productCount, domainCount });

            // Validate the purchase
            // Ranked and named as the plan cards are: Plan Management IDs such as
            // "YEARLY STARTER PLAN" aren't PLAN_HIERARCHY keys (issue #286)
            const validation = await planPurchaseService.validatePurchase(
                plan.plan_id,
                currentPlanInfo,
                productCount,
                domainCount,
                { newPlanName: plan.name, comparePlans, validityDays: plan.validity_days }
            );

            console.log('✅ Validation result:', validation);

            // Format messages for display
            const messages = planPurchaseService.formatValidationMessages(validation);

            // ═══════════════════════════════════════════════════════════════
            // STEP 3B: HANDLE VALIDATION RESULT
            // ═══════════════════════════════════════════════════════════════

            // BLOCKED: Cannot proceed
            if (!validation.allowed) {
                toast.error(
                    <div>
                        <strong>{messages.title}</strong>
                        <div style={{ marginTop: '10px' }}>
                            {messages.blockers.map((blocker, idx) => (
                                <div key={idx} style={{ marginTop: '5px', fontSize: '0.9em' }}>
                                    {blocker}
                                </div>
                            ))}
                        </div>
                    </div>,
                    { autoClose: 8000, toastId: 'plan-purchase-blocked' }
                );
                return;
            }

            // CONFIRMATION REQUIRED: Show warning modal
            if (validation.confirmationRequired && !confirmed) {
                setPendingPurchase({
                    plan,
                    validation,
                    messages
                });
                setShowConfirmModal(true);
                return;
            }

            // NO ISSUES: Proceed directly
            console.log('✅ No issues, proceeding with purchase');
            await proceedWithPurchase(plan);

        } catch (error) {
            console.error('Error validating purchase:', error);
            toast.error('Failed to validate purchase. Please try again.');
        } finally {
            buyingRef.current = false;
        }
    };

    // STEP 3C: Proceed with actual purchase (after validation)
    const proceedWithPurchase = async (plan) => {
        try {
            // Razorpay's script first: no order for a checkout that can't open
            if (!(await loadRazorpayCheckout())) {
                toast.error(CHECKOUT_UNAVAILABLE_MESSAGE, { toastId: 'checkout-unavailable' });
                return;
            }
            console.log('💳 Creating order for plan:', plan.plan_id);
            
            // Create order with discount_id if available
            console.log('💰 Plan discount info:', {
                has_discount: plan.has_discount,
                discount: plan.discount,
                discount_id: plan.has_discount && plan.discount ? plan.discount.discount_id : null
            });
            
            const orderResponse = await api.post('/plan/order/create', {
                business_user_id: businessUserId,
                plan_id: plan.plan_id,
                discount_id: plan.has_discount && plan.discount ? plan.discount.discount_id : null
            });


            // Check if response is successful
            if (orderResponse.data.status !== 'success') {
                throw new Error(orderResponse.data.message || 'Failed to create order');
            }

            const { order_id, amount, plan_name, razorpay_key } = orderResponse.data.result;

            // The key comes from the backend, which created the order: .env test keys
            // locally (USE_LOCAL_ENV), AWS Secrets Manager in production. Never a key built
            // into this app, which could belong to a different Razorpay account.
            if (!razorpay_key) {
                throw new Error('Payment is not configured. Please contact support.');
            }

            // Validate required fields
            if (!order_id || !amount) {
                throw new Error('Invalid order response from server');
            }

            console.log('Opening Razorpay with order_id:', order_id);
            // Checked when the seller comes back, in case the handler never runs
            savePendingPayment(order_id);

            // Check if Razorpay is loaded
            if (!Razorpay) {
                toast.error('Payment system is loading. Please try again.');
                return;
            }

            // STEP 4: Open Razorpay Checkout (must match backend key)
            const options = {
                key: razorpay_key, // Same Razorpay account that created the order
                amount: Math.round(parseFloat(amount) * 100), // Amount in paise
                currency: 'INR',
                name: 'MyTamilKart',
                description: `Purchase ${plan_name}`,
                order_id: order_id,
                handler: async function (response) {
                    console.log('Payment successful:', response);
                    // STEP 5: Call backend to verify payment
                    await verifyPayment(response, plan.plan_id);
                },
                prefill: {
                    name: '',
                    email: '',
                    contact: ''
                },
                theme: {
                    color: '#1E5B3A',
                    backdrop_color: 'rgba(0, 0, 0, 0.6)'
                },
                modal: {
                    ondismiss: function() {
                        console.log('Payment modal dismissed');
                        toast.info('Payment cancelled');
                    },
                    escape: true,
                    confirm_close: true, // Show confirmation before closing
                    animation: true,
                    backdropclose: false, // Prevent closing by clicking backdrop
                    handleback: true // Enable back button handling on mobile
                },
                config: {
                    display: {
                        blocks: {
                            banks: {
                                name: 'All payment methods',
                                instruments: [
                                    {
                                        method: 'upi'
                                    },
                                    {
                                        method: 'card'
                                    },
                                    {
                                        method: 'netbanking'
                                    },
                                    {
                                        method: 'wallet'
                                    }
                                ]
                            }
                        },
                        sequence: ['block.banks'],
                        preferences: {
                            show_default_blocks: true
                        }
                    }
                }
            };


            const rzp = new Razorpay(options);
            
            rzp.on('payment.failed', function (response) {
                console.error('Payment failed:', response.error);
                toast.error(`Payment failed: ${response.error.description || 'Please try again'}`);
            });

            // Open Razorpay
            rzp.open();
        } catch (error) {
            console.error('Error creating order:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to create order. Please try again.';
            toast.error(errorMessage);
        }
    };

    // Handle confirmation modal actions
    const handleConfirmPurchase = async () => {
        setShowConfirmModal(false);
        if (pendingPurchase) {
            await proceedWithPurchase(pendingPurchase.plan);
            setPendingPurchase(null);
        }
    };

    const handleCancelPurchase = () => {
        setShowConfirmModal(false);
        setPendingPurchase(null);
        toast.info('Purchase cancelled');
    };

    // No / Cancel: the seller keeps their plan and goes to manage their store
    const handleKeepPlan = () => {
        setPlanSwitch(null);
        navigate('/gift', { replace: true });
    };

    // Continue: Razorpay for the higher plan; payment moves the seller to it
    const handleSwitchPlan = () => {
        const { plan } = planSwitch;
        setPlanSwitch(null);
        handleBuyPlan(plan, { confirmed: true });
    };

    // Open checkout for the picked plan once, after the plans and the current plan
    // have loaded: the same validation and order as clicking its Get Started
    useEffect(() => {
        if (!pickedPlanId || pickedPlanHandled.current || loading || !businessUserId || !currentPlanLoaded) return;
        pickedPlanHandled.current = true;
        clearPendingPlan(); // a later login goes to the products as usual
        if (!pickedPlan) {
            toast.info('The plan you picked is no longer available. Please choose a plan below.');
            return;
        }
        const hasActivePlan = currentPlanId && !(currentPlanInfo?.is_expired || currentPlanInfo?.plan_status === 'expired');
        if (!hasActivePlan) {
            handleBuyPlan(pickedPlan);
            return;
        }
        // Only a higher plan can be bought (issue #282, same rule as the cards). Their own
        // plan, a lower one, or one that can't be ranked against theirs (e.g. a legacy
        // plan not in the list): nothing to buy, so straight to the admin site
        const isSamePlan = String(currentPlanId) === String(pickedPlan.plan_id);
        const versusCurrent = comparePlans(pickedPlan.plan_id, currentPlanId);
        if (isSamePlan || versusCurrent <= 0) {
            toast.info(!isSamePlan && versusCurrent < 0
                ? `You're on the ${currentPlanName} plan, which already includes ${pickedPlan.name}.`
                : `You're already on the ${currentPlanName} plan.`);
            navigate('/gift', { replace: true });
            return;
        }
        // A higher plan: ask before opening checkout
        setPlanSwitch({ plan: pickedPlan, step: 'ask' });
    }, [pickedPlanId, loading, businessUserId, currentPlanLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // STEP 5: Verify payment and activate plan
    // The plan is active: on to the products page. From the checkout handler or
    // a reconcile (both can answer for one payment, hence the toastId)
    const planActivated = ({ plan_name, expires_on } = {}) => {
        clearPendingPlan();
        clearPendingPayment();
        toast.success(expires_on
            ? `Success! ${plan_name} activated. Expires on: ${expires_on}`
            : `Success! ${plan_name} activated.`, { toastId: 'plan-activated' });
        setTimeout(() => {
            // Same site, so the Store Ready hand-off (sessionStorage) survives, on every environment
            window.location.href = `/gift?plan_update=success&lang=${i18n.language}`;
        }, 2000);
    };

    // A mobile UPI payment: the seller pays in the UPI app and the tab is suspended
    // or reloaded, so the checkout handler never verifies it. When they come back
    // to this page, the API activates the order Razorpay captured (issue #286)
    // A UPI payment can take a few seconds to be captured after the seller is back,
    // so a check that finds it unpaid is repeated: every RECONCILE_RETRY_MS, up to
    // RECONCILE_RETRIES times, while the page is open
    // `followUp`: one of those repeats, which the focus / visibility throttle doesn't apply to
    const reconcilePayment = async (retriesLeft = RECONCILE_RETRIES, followUp = false) => {
        if (unmountedRef.current || !businessUserId || reconcilingRef.current || !getPendingPayment()) return;
        if (!followUp && lastReconcileRef.current && Date.now() - lastReconcileRef.current < RECONCILE_THROTTLE_MS) return;
        clearTimeout(reconcileTimerRef.current);
        reconcilingRef.current = true;
        lastReconcileRef.current = Date.now();
        let activated = false;
        let notPaid = false;
        try {
            const response = await api.post('/plan/payment/reconcile', {});
            const result = response.data?.result;
            if (unmountedRef.current) {
                // Left the plans page meanwhile: the next visit (or the webhook) takes over
                return;
            }
            if (response.data?.status === 'success' && result?.activated) {
                activated = true;
                planActivated(result);
            } else if (response.data?.status === 'success') {
                notPaid = true;
            }
        } catch (error) {
            console.error('Error checking the plan payment:', error);
        } finally {
            reconcilingRef.current = false;
        }
        // Still unpaid long after checkout opened: a cancelled or abandoned checkout,
        // so stop asking Razorpay (the webhook activates a late payment)
        if (notPaid && pendingPaymentAge() > PENDING_PAYMENT_GIVE_UP_MS) {
            clearPendingPayment();
            return;
        }
        if (!activated && !unmountedRef.current && retriesLeft > 0 && document.visibilityState === 'visible') {
            reconcileTimerRef.current = setTimeout(() => reconcilePayment(retriesLeft - 1, true), RECONCILE_RETRY_MS);
        }
    };

    useEffect(() => {
        if (!businessUserId) return undefined;
        unmountedRef.current = false;
        // Follow-ups on load only for a checkout opened moments ago (see RECENT_CHECKOUT_MS)
        const age = pendingPaymentAge();
        reconcilePayment(age !== null && age < RECENT_CHECKOUT_MS ? RECONCILE_RETRIES : 0);
        const onReturn = () => {
            if (document.visibilityState === 'visible') reconcilePayment();
        };
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);
        return () => {
            unmountedRef.current = true;
            clearTimeout(reconcileTimerRef.current);
            document.removeEventListener('visibilitychange', onReturn);
            window.removeEventListener('focus', onReturn);
        };
    }, [businessUserId]); // eslint-disable-line react-hooks/exhaustive-deps

    const verifyPayment = async (razorpayResponse, planId) => {
        try {
            console.log('Verifying payment:', razorpayResponse);
            
            const response = await api.post('/plan/payment/verify', {
                business_user_id: businessUserId,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
                plan_id: planId
            });

            console.log('Verification response:', response.data);

            if (response.data.status === 'success') {
                planActivated(response.data.result);
            } else {
                throw new Error(response.data.message || 'Payment verification failed');
            }
        } catch (error) {
            console.error('Payment verification failed:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Payment verification failed. Please contact support.';
            toast.error(errorMessage);
        }
    };

    const isPlanExpired = currentPlanInfo?.is_expired ||
        currentPlanInfo?.plan_status === 'expired' ||
        !currentPlanId;
    // The comparison table has three tiers, cheapest first: headed with the
    // plan names from Plan Management when there are exactly three plans
    const featureColumns = filteredPlans.length === 3 ? filteredPlans.map(p => p.name) : ['Starter', 'Pro', 'Premium'];
    const hasPaidPlan = currentPlanInfo && currentPlanInfo.plan_name &&
        currentPlanInfo.plan_name.toLowerCase() !== 'unknown' &&
        currentPlanInfo.plan_name.toLowerCase() !== 'unknown plan' &&
        currentPlanInfo.plan_status !== 'cancelled' &&
        currentPlanInfo.plan_status !== 'created';

    const toggleTier = (tier) => {
        if (shownTiers.includes(tier)) {
            if (shownTiers.length === 1) {
                toast.info('Keep at least one plan selected');
                return;
            }
            setShownTiers(shownTiers.filter(x => x !== tier));
        } else {
            setShownTiers(TIERS.filter(x => x === tier || shownTiers.includes(x)));
        }
    };
    const isRowShown = (row) => {
        if (!diffOnly || shownTiers.length < 2) return true;
        const values = shownTiers.map(tier => String(row[tier + 1]));
        return !values.every(value => value === values[0]);
    };

    const renderPrice = (plan) => {
        const listPrice = plan.original_price || plan.price;
        if (plan.has_discount && plan.discount) {
            return (
                <>
                    <span className="pp-deal">{plan.discount.discount_name}</span>
                    <div className="pr2">
                        <b>₹{formatPrice(plan.discount.final_price)}</b>
                        <span>/year</span>
                        <s>₹{formatPrice(plan.original_price)}</s>
                    </div>
                    <div className="meta">
                        <em>
                            {plan.discount.discount_type === 'percentage'
                                ? `${plan.discount.discount_value}% OFF`
                                : `₹${plan.discount.discount_amount} OFF`}
                        </em>
                        <span>Save ₹{formatPrice(plan.discount.discount_amount)}</span>
                        <CountdownTimer endDate={plan.discount.end_date} />
                    </div>
                </>
            );
        }
        // Crossed-out price: 12 months at one tenth of the yearly price
        const wasPrice = isYearlyPlan(plan) ? crossedOutPrice(listPrice) : null;
        return (
            <div className="pr2">
                <b>₹{formatPrice(listPrice)}</b>
                <span>/year</span>
                {wasPrice !== null && <s>₹{wasPrice.toLocaleString('en-IN')}</s>}
            </div>
        );
    };

    // A lower plan is bought like any other: Get started, then the purchase checks
    // ask the seller to confirm the downgrade (or block it when their products or
    // domains don't fit)
    const renderCta = (plan, { isCurrentPlan, isUpgrade, isHighlighted }) => {
        if (isCurrentPlan) {
            return (
                <button type="button" className="pp-btn cur" disabled>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-10" /></svg>
                    Current plan
                </button>
            );
        }
        return (
            <button type="button" className={`pp-btn ${isHighlighted ? 'o' : 'gh'}`} onClick={() => handleBuyPlan(plan)}>
                {plan.has_discount && plan.discount ? 'Get discount now'
                    : isPlanExpired && currentPlanId === plan.plan_id ? 'Renew plan'
                    : isUpgrade ? 'Upgrade plan'
                    : 'Get started'}
            </button>
        );
    };

    // The plan switch dialog: the seller's number, and how the picked plan differs
    const switchDifferences = planSwitch ? planDifferences(currentPlan, planSwitch.plan) : [];

    const messages = pendingPurchase?.messages;
    const isDowngradeConfirm = pendingPurchase?.validation?.scenario === 'downgrade';

    return (
        <main className="mtk-pp" id="top">
            <div className="pp-w">
                <div className="pp-top">
                    <button type="button" className="pp-back" aria-label="Back to products" onClick={(e) => { e.preventDefault(); navigate('/gift'); }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    </button>
                    {hasPaidPlan && (
                        <button type="button" className="pp-inv" onClick={(e) => { e.preventDefault(); navigate('/my-invoices'); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>
                            My Invoices
                        </button>
                    )}
                </div>

                <section className="ph-hero" aria-label="Pricing">
                    <span className="k">விலைப்பட்டியல் · Pricing</span>
                    <span className="billnote">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
                        Annual plans · Billed yearly · Prices + GST
                    </span>
                </section>

                {loading ? (
                    // Placeholder cards, so no price flashes and then changes
                    <section className="plans2" aria-label="Plans" aria-busy="true">
                        {TIERS.map(tier => (
                            <article key={tier} className={`pc${tier === POPULAR_TIER ? ' pop' : ''}`}>
                                <h2><span className="pp-skel" style={{ width: '40%', height: '28px' }} /></h2>
                                <div className="pr2"><span className="pp-skel" style={{ width: '60%', height: '40px' }} /></div>
                                <span className="pp-skel" style={{ width: '100%', height: '52px', borderRadius: '99px' }} />
                            </article>
                        ))}
                    </section>
                ) : filteredPlans.length === 0 ? (
                    <div className="pp-empty" role="status">Our annual plans are being updated. Please check back soon.</div>
                ) : (
                    <section className="plans2" aria-label="Plans">
                        {filteredPlans.map((plan, index) => {
                            // The middle plan is the popular one
                            const isPopular = index === POPULAR_TIER || plan.plan_id.toLowerCase() === 'pro';
                            const isPicked = plan === pickedPlan;
                            // The picked plan is outlined; with none picked, the popular one is
                            const isHighlighted = pickedPlan ? isPicked : isPopular;

                            // PLAN_HIERARCHY, or the list price for other plan IDs (comparePlans)
                            const versusCurrent = currentPlanId ? comparePlans(plan.plan_id, currentPlanId) : 0;
                            const isCurrentPlan = !isPlanExpired && currentPlanId === plan.plan_id;
                            const isUpgrade = currentPlanId && versusCurrent > 0;
                            const tamilName = TAMIL_NAMES[plan.name.toLowerCase()];

                            return (
                                <article
                                    key={plan.plan_id}
                                    className={`pc${isHighlighted ? ' pop' : ''}`}
                                    aria-labelledby={`plan-${index}`}
                                    aria-current={isPicked ? 'true' : undefined}
                                >
                                    {isPopular && <span className="tagpop">★ Most popular</span>}
                                    <h2 id={`plan-${index}`}>
                                        {plan.name}
                                        {tamilName && <small>{tamilName}</small>}
                                    </h2>
                                    {renderPrice(plan)}
                                    {renderCta(plan, { isCurrentPlan, isUpgrade, isHighlighted })}
                                </article>
                            );
                        })}
                    </section>
                )}

                <section className="cmp" aria-labelledby="h-cmp">
                    <div className="cmp-h"><h2 id="h-cmp">Features</h2></div>
                    <div className="tblwrap">
                        <table className="ft">
                            <colgroup>
                                <col className="c0" />
                                {shownTiers.map(tier => <col key={tier} />)}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col">Features</th>
                                    {shownTiers.map(tier => (
                                        <th key={tier} scope="col" className={tier === POPULAR_TIER ? 'pop' : undefined}>{featureColumns[tier]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {FEATURES.map(([group, tamil, rows]) => {
                                    const shownRows = rows.filter(isRowShown);
                                    if (shownRows.length === 0) return null;
                                    return [
                                        <tr key={group} className="grp">
                                            <th scope="rowgroup" colSpan={shownTiers.length + 1}>{group}<span>{tamil}</span></th>
                                        </tr>,
                                        ...shownRows.map(row => (
                                            <tr key={row[0]}>
                                                <th scope="row">{row[0]}</th>
                                                {shownTiers.map(tier => (
                                                    <td key={tier} className={tier === POPULAR_TIER ? 'pop' : undefined}>{featureValue(row[tier + 1])}</td>
                                                ))}
                                            </tr>
                                        )),
                                    ];
                                })}
                            </tbody>
                        </table>
                    </div>
                    <fieldset className="pick">
                        <legend>Compare plans</legend>
                        {TIERS.map(tier => (
                            <label key={tier} className="tgl">
                                <input type="checkbox" checked={shownTiers.includes(tier)} onChange={() => toggleTier(tier)} />
                                {featureColumns[tier]}
                            </label>
                        ))}
                        <span className="sep" aria-hidden="true" />
                        <label className="tgl">
                            <input type="checkbox" checked={diffOnly} onChange={e => setDiffOnly(e.target.checked)} />
                            Show only differences
                        </label>
                    </fieldset>
                </section>
            </div>

            {/* A higher plan picked on mytamilkart.com by a seller who already has one */}
            {planSwitch && (
                // Escape closes it the same way as No / Cancel. tabIndex -1: a click on the
                // overlay keeps focus in the dialog (not <body>), so Escape still works
                <div className="mtk-pp-modal" role="dialog" aria-modal="true" aria-labelledby="pp-switch-title"
                    tabIndex={-1} onKeyDown={(e) => { if (e.key === 'Escape') handleKeepPlan(); }}>
                    {/* Keys: each step mounts fresh, so its autoFocus runs */}
                    {planSwitch.step === 'ask' ? (
                        <div className="box" key="ask">
                            <h2 id="pp-switch-title">You already have a plan</h2>
                            <p>
                                {sellerPhone ? `${sellerPhone} already has` : "You're on"} the {currentPlanName} plan.
                                {' '}Do you want to upgrade to {planSwitch.plan.name}?
                            </p>
                            <div className="acts">
                                <button type="button" className="pp-btn gh" onClick={handleKeepPlan}>No</button>
                                <button type="button" className="pp-btn o" autoFocus onClick={() => setPlanSwitch({ ...planSwitch, step: 'compare' })}>Yes</button>
                            </div>
                        </div>
                    ) : (
                        <div className="box wide" key="compare">
                            <h2 id="pp-switch-title" tabIndex={-1} ref={compareHeadingRef}>{currentPlanName} → {planSwitch.plan.name}</h2>
                            <table className="pp-diff" aria-label="What changes">
                                <thead>
                                    <tr>
                                        <td />
                                        <th scope="col">{currentPlanName}</th>
                                        <th scope="col">{planSwitch.plan.name}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {switchDifferences.map(([label, from, to]) => (
                                        <tr key={label}>
                                            <th scope="row">{label}</th>
                                            <td>{featureValue(from)}</td>
                                            <td>{featureValue(to)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p>
                                {planSwitch.plan.name} starts as soon as you pay and runs for a year.
                                {' '}The days left on {currentPlanName} aren't carried over.
                            </p>
                            <p className="q">Continue to payment?</p>
                            <div className="acts">
                                <button type="button" className="pp-btn gh" onClick={handleKeepPlan}>Cancel</button>
                                <button type="button" className="pp-btn o" onClick={handleSwitchPlan}>Continue</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Confirmation for upgrades / downgrades */}
            {showConfirmModal && pendingPurchase && (
                <div className="mtk-pp-modal" role="dialog" aria-modal="true" aria-labelledby="pp-confirm-title">
                    <div className={`box${isDowngradeConfirm ? ' down' : ''}`}>
                        <h2 id="pp-confirm-title">{messages.title}</h2>
                        {messages.description && <p>{messages.description}</p>}
                        {messages.gains && messages.gains.length > 0 && (
                            <div className="lst gain">
                                <h3>What you'll gain</h3>
                                {messages.gains.map((gain, idx) => <div key={idx}>{gain}</div>)}
                            </div>
                        )}
                        {messages.losses && messages.losses.length > 0 && (
                            <div className="lst loss">
                                <h3>What you'll lose</h3>
                                {messages.losses.map((loss, idx) => <div key={idx}>{loss}</div>)}
                            </div>
                        )}
                        {messages.warnings && messages.warnings.length > 0 && (
                            <div className="lst warn">
                                <h3>Important</h3>
                                {messages.warnings.map((warning, idx) => <div key={idx}>{warning}</div>)}
                            </div>
                        )}
                        <p className="q">Do you want to proceed with this purchase?</p>
                        <div className="acts">
                            <button type="button" className="pp-btn gh" onClick={handleCancelPurchase}>Cancel</button>
                            <button type="button" className={`pp-btn ${isDowngradeConfirm ? 'danger' : 'o'}`} onClick={handleConfirmPurchase}>
                                Yes, proceed to payment
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

export default Plan;
